'use client';

/**
 * ✅ ACTIVE VERSION - Used by MortgageMarketingROICalculator.tsx
 *
 * This is the CURRENT/CORRECT version of HybridSystemModal used on /mortgage-marketing-roi-calculator
 *
 * NOTE: There is a v3 version at /src/components/tools/mortgage-calculator/v3/components/HybridSystemModal.tsx
 * but it is NOT currently used in production. This file is the active version.
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp, Target, Rocket, BarChart3, Zap, Activity } from '@/lib/icons';

interface HybridSystemModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HybridSystemModal: React.FC<HybridSystemModalProps> = ({ isOpen, onClose }) => {
  // Tab state management
  const [activeTab, setActiveTab] = React.useState<'system' | 'how-it-works'>('how-it-works');
  const contentRef = React.useRef<HTMLDivElement>(null);

  // Prevent scroll when modal is open
  React.useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset scroll position when tab changes
  React.useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [activeTab]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.3, type: "spring", damping: 25 }}
            className="fixed inset-0 flex items-center justify-center z-50 p-0 lg:p-4"
            style={{ pointerEvents: 'none' }}
          >
            <div
              className="bg-white w-full h-[100dvh] rounded-none lg:max-w-[800px] lg:max-h-[850px] lg:max-h-[90vh] lg:rounded-lg shadow-2xl overflow-hidden flex flex-col"
              style={{ pointerEvents: 'auto' }}
            >
              {/* Gradient accent bar */}
              <div
                className="h-1"
                style={{
                  background: 'linear-gradient(to right, #294bfd, #ff34a7)'
                }}
              />

              {/* Tab Navigation */}
              <div className="bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8">
                <div className="flex">

                <button
                  onClick={() => setActiveTab('how-it-works')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 ${
                    activeTab === 'how-it-works'
                      ? 'text-blue-500 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 border-transparent'
                  }`}
                >
                  How It Works
                </button>
                <button
                  onClick={() => setActiveTab('system')}
                  className={`px-6 py-4 text-sm font-medium border-b-2 ${
                    activeTab === 'system'
                      ? 'text-blue-500 border-blue-500'
                      : 'text-gray-500 hover:text-gray-700 border-transparent'
                  }`}
                >
                  The Hybrid System
                </button>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

            {/* Content Container */}
            <div ref={contentRef} className="flex-1 overflow-y-auto px-4 pt-4 pb-2 lg:px-8 lg:pt-6 lg:pb-1">
              {activeTab === 'system' ? (
                // Tab 1: The Hybrid System
                <div className="space-y-3">
                  <div className="flex items-center gap-3 mb-4">
                    <TrendingUp className="w-8 h-8 text-blue-500" />
                    <h2 className="text-2xl font-bold text-gray-900">Stop Needing Referrals. Get More Referrals.</h2>
                  </div>

                  <div className="text-sm text-gray-700 leading-normal space-y-2">
                    <p>Traditional loan officers depend on others for leads. Hybrid loan officers generate their own leads AND get more referrals because of it.</p>
                    <p>When you bring agents qualified buyers and sellers instead of asking for favors, the dynamic flips. You have value. You have leverage. You become essential.</p>
                  </div>

                  <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-3 border-2 border-blue-200">
                    <div className="flex items-center gap-2 mb-3">
                      <Zap className="w-6 h-6 text-blue-500" />
                      <h3 className="text-base font-semibold text-gray-900">This calculator models both sides:</h3>
                    </div>
                    <div className="space-y-3 text-sm text-gray-700 leading-normal">
                      <p><span className="font-bold">The Engine</span> - Direct lead generation through paid ads. Your foundation of self-sufficiency.</p>
                      <div>
                        <p className="mb-2"><span className="font-bold">The Multipliers</span> - What happens when you have leads to share:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4">
                          <li>Agents send referrals because you bring them buyers and listings</li>
                          <li>Your closed loans generate future referrals</li>
                          <li>Unconverted leads reactivate through systematic nurture</li>
                          <li>Your name recognition drives organic traffic</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-rose-50 to-white rounded-lg p-3 border border-rose-200">
                    <div className="flex items-center gap-2 mb-3">
                      <BarChart3 className="w-6 h-6 text-rose-600" />
                      <h3 className="text-base font-semibold text-gray-900">Reality check on running paid ads for mortgage <span className="font-normal text-sm">- $2,500/month ad spend (benchmark):</span></h3>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-700 leading-normal">
                      <li><span className="font-bold">Most people:</span> 0-1 loans, quit after 3 months</li>
                      <li><span className="font-bold">Good execution:</span> 1-2 loans, can be profitable</li>
                      <li><span className="font-bold">Hybrid System:</span> 5-8 loans, predictably profitable and scalable</li>
                    </ul>
                  </div>

                  <div className="text-sm text-gray-700 leading-normal space-y-2 mt-5">
                    <p>The difference isn't magic. It's systematic activation of multipliers most never build.</p>
                    <p>Test your market. Run different scenarios. See if this model works for your situation.</p>
                  </div>
                </div>
              ) : (
                // Tab 2: How It Works
                <div className="space-y-3">
                  <div className="flex items-center gap-3 mb-4">
                    <Activity className="w-8 h-8 text-blue-500" />
                    <h2 className="text-2xl font-bold text-gray-900">Two Calculations Tell Different Stories</h2>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-white rounded-lg p-4 border-2 border-purple-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="w-6 h-6 text-purple-600" />
                      <h3 className="text-base font-semibold text-gray-900">Paid Ads Only</h3>
                    </div>
                    <p className="text-sm text-gray-700">Direct returns from ad spend. Often negative early on. Break-even with good execution. Profitable only with excellent optimization.</p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-white rounded-lg p-4 border-2 border-green-100">
                    <div className="flex items-center gap-2 mb-2">
                      <Rocket className="w-6 h-6 text-green-600" />
                      <h3 className="text-base font-semibold text-gray-900">Hybrid System</h3>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">Includes four Revenue Multipliers:</p>
                    <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 ml-4">
                      <li>Agent Partnerships: Share leads with producing agents to unlock more referrals</li>
                      <li>Client Referrals: Every closed loan creates future opportunities</li>
                      <li>Pipeline Nurture: Dead leads aren't dead, just not ready yet</li>
                      <li>Brand/Organic: People see your ads, search for you later</li>
                    </ul>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2">Two Ways To View Results</h3>

                  <div className="space-y-3 text-sm text-gray-700 leading-relaxed">
                    <p><span className="font-bold">Snapshot</span> - Shows month 12 when everything's running. Your mature business model.</p>
                    <p><span className="font-bold">Timeline</span> - Maps the journey from day 1. When you break even, when multipliers activate, when profit compounds.</p>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2">Reading The Numbers</h3>

                  <div className="text-sm text-gray-700 leading-relaxed space-y-3">
                    <p>Start conservative. See what paid ads alone produce. Add multipliers based on your reality. The gap shows what you're leaving on the table.</p>
                    <p>Most people never model this. They guess, spend, fail, quit. This shows you the math before you spend.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex-shrink-0 flex justify-center">
              <button
                onClick={onClose}
                className="px-6 py-2.5 bg-gradient-to-r from-[#294bfd] to-[#ff34a7] text-white font-medium rounded-lg hover:shadow-lg transition-all"
              >
                Let's See My Numbers
              </button>
            </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
